/**
 * Non-color design tokens (font stacks, shadows, shared sx fragments) used by
 * the MUI theme and by components. Kept free of "use client" so server
 * components can import the plain string values directly. Font families
 * reference the CSS variables set by next/font in the root layout.
 */

export const fonts = {
  // Display serif. Fraunces carries no CJK glyphs, so Japanese headings fall
  // back to a mincho serif — an intentional editorial pairing.
  heading:
    'var(--font-display), Fraunces, "Hiragino Mincho ProN", "Yu Mincho", Georgia, serif',
  body: "var(--font-body), Inter, system-ui, -apple-system, sans-serif",
  mono: "var(--font-mono), ui-monospace, SFMono-Regular, Menlo, monospace",
} as const;

export const shadows = {
  // Editorial surfaces are ruled, not elevated: a single hairline underline.
  card: "0 1px 0 rgba(31, 27, 22, 0.06)",
  cardHover: "0 1px 0 rgba(31, 27, 22, 0.12)",
} as const;

/**
 * Pricing-card title. The 2-line `minHeight` is shared by every card so a title
 * that wraps (e.g. "Native iPhone app") reserves the same height as a one-line
 * title, keeping the price rows aligned across the row of cards.
 */
export const cardTitleSx = {
  fontSize: "1.5rem",
  minHeight: "2.6em",
  mb: 1.5,
} as const;

/** Large package price display, shared by the pricing cards and Care add-on. */
export const priceSx = {
  fontFamily: fonts.heading,
  fontWeight: 400,
  color: "text.primary",
  fontSize: "clamp(1.75rem, 2.6vw, 2.4rem)",
  letterSpacing: "-0.01em",
  lineHeight: 1.1,
} as const;

/**
 * The [company name] wordmark, shared by every surface that renders it (landing
 * header, inner-page top bar, footer, onboarding, press kit) so the brand name
 * is always one font, weight, and color. Callers set their own `fontSize`.
 */
export const wordmarkSx = {
  fontFamily: fonts.heading,
  fontWeight: 480,
  letterSpacing: "0",
  color: "text.primary",
} as const;

/** Small mono uppercase eyebrow/kicker above a heading. Callers add margin. */
export const eyebrowSx = {
  fontFamily: fonts.mono,
  textTransform: "uppercase",
  letterSpacing: "0.22em",
  fontSize: "0.7rem",
  fontWeight: 600,
  color: "primary.main",
} as const;

/**
 * Oversized italic serif numeral (e.g. the 404 mark on the not-found page).
 * Muted gray at reduced opacity so it reads as printed structure.
 */
export const chapterNumeralSx = {
  fontFamily: fonts.heading,
  fontWeight: 300,
  fontStyle: "italic",
  fontSize: "clamp(4rem, 9vw, 8rem)",
  lineHeight: 0.9,
  color: "secondary.main",
  opacity: 0.55,
} as const;

/**
 * Vertical rhythm shared by every landing-page section. The scroll margin
 * keeps anchored sections clear of the 64px sticky header on #hash navigation.
 */
export const sectionSx = {
  py: { xs: 10, md: 16 },
  scrollMarginTop: 80,
} as const;

/** Section-level h2 heading. */
export const sectionTitleSx = {
  fontSize: "clamp(2.25rem, 4.5vw, 3.75rem)",
  mb: 2,
} as const;

/** Lede paragraph under a section title. */
export const sectionSubtitleSx = {
  mb: { xs: 6, md: 8 },
  maxWidth: 600,
  fontSize: "1.1rem",
} as const;

/** h1 on standalone content pages (FAQ, press kit). */
export const pageTitleSx = {
  fontSize: "clamp(2.5rem, 5vw, 4rem)",
  mb: 2,
} as const;

/** Lede paragraph under a standalone page title. */
export const pageIntroSx = {
  fontSize: "1.05rem",
  lineHeight: 1.7,
  color: "text.secondary",
  mb: 6,
  maxWidth: 640,
} as const;

/** h2 for a section within a standalone content page (work, about). */
export const sectionHeadingSx = {
  fontSize: "1.75rem",
  mb: 3,
} as const;

/** Header/nav label: small mono uppercase, quiet until hovered. */
export const navLabelSx = {
  fontFamily: fonts.mono,
  textTransform: "uppercase",
  letterSpacing: "0.14em",
  fontSize: "0.72rem",
  fontWeight: 400,
} as const;

/** Full-width 1px rule used to divide editorial sections and rows. */
export const hairlineSx = {
  borderTop: "1px solid",
  borderColor: "divider",
} as const;

/** Small mono folio index ("01", "02") in muted gray — the editorial page-furniture
 *  label used by the mobile drawer index. */
export const folioSx = {
  fontFamily: fonts.mono,
  fontSize: "0.7rem",
  letterSpacing: "0.18em",
  color: "secondary.main",
} as const;

/** Vertical rhythm for standalone content pages (work, about, press, FAQ). */
export const pageContainerSx = {
  py: { xs: 6, md: 9 },
} as const;

/**
 * Pricing rate-card column: paper panel with hairline borders; selection reads
 * as a violet rule across the top edge. Shared by the package cards and the
 * Care add-on so the selected states stay identical.
 */
export const pricingCardSx = (selected: boolean) =>
  ({
    display: "flex",
    flexDirection: "column",
    p: { xs: 3, md: 3.5 },
    backgroundColor: "background.paper",
    boxShadow: "none",
    border: "1px solid",
    borderColor: "divider",
    borderTop: "3px solid",
    borderTopColor: selected ? "primary.main" : "divider",
    transition: "border-color 0.15s ease",
  }) as const;
